import {
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  useCallback,
} from 'react';
import {DIGIT_REGEX, extractDigits, useInputOTPContext} from './InputOTPContext';

export function useInputOTPSlot(index: number) {
  const {
    slots,
    activeIndex,
    disabled,
    error,
    handleDigitInput,
    handleDelete,
    handlePaste,
    setActiveIndex,
    inputRefs,
    maxLength,
  } = useInputOTPContext();

  const char = slots[index] ?? '';
  const isActive = activeIndex === index;

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Backspace':
      case 'Delete':
        event.preventDefault();
        handleDelete(index);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (index > 0) {
          setActiveIndex(index - 1);
          inputRefs.current[index - 1]?.focus();
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (index < maxLength - 1) {
          setActiveIndex(index + 1);
          inputRefs.current[index + 1]?.focus();
        }
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        inputRefs.current[0]?.focus();
        break;
      case 'End': {
        event.preventDefault();
        const lastIndex = maxLength - 1;
        setActiveIndex(lastIndex);
        inputRefs.current[lastIndex]?.focus();
        break;
      }
      default:
        if (DIGIT_REGEX.test(event.key)) {
          event.preventDefault();
          handleDigitInput(index, event.key);
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          event.preventDefault();
        }
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = extractDigits(event.target.value);

    if (digits.length > 1) {
      handlePaste(digits);
    } else if (digits.length === 1) {
      handleDigitInput(index, digits);
    }
  };

  const handlePasteEvent = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    handlePaste(event.clipboardData.getData('text/plain'));
  };

  const setInputRef = useCallback(
    (element: HTMLInputElement | null) => {
      inputRefs.current[index] = element;
    },
    [index, inputRefs],
  );

  const focusSlot = useCallback(() => {
    inputRefs.current[index]?.focus();
  }, [index, inputRefs]);

  return {
    char,
    isActive,
    disabled,
    error,
    maxLength,
    setInputRef,
    handleKeyDown,
    handleInputChange,
    handlePasteEvent,
    focusSlot,
  };
}
